import { useCallback, useMemo, useState } from 'react';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GET_MONTHLY_TAM_PRIME, VALIDATE_MONTHLY_PRIME } from '@bloomays-lib/adapter.api-talent';
import { Role } from '@bloomays-lib/types.shared';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useActiveTams } from '../../hooks/useActiveTams';
import { formatMonthYearFr } from '@bloomays-lib/utils.shared';
import { useAuthStore } from '../../stores';
import { errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';
import MonthlyPrimeHeader from '../molecules/MonthlyPrimeHeader';
import TamRegularisationModal from '../molecules/TamRegularisationModal';
import MonthlyPrimeTable from '../molecules/MonthlyPrimeTable';
import {
  GetMonthlyTamPrimeQueryData,
  MonthlyTamPrimeQueryResult,
  TamPrimeLine,
  isValidatedMonthlyTamPrime,
} from '../../types/primes';
import {
  canValidateMonthlyPrime,
  deriveHeaderAmounts,
  getDefaultPrimeReferenceDate,
  getTamDisplayName,
  hasIneligibleMissionCras,
  hasPrimeLineErrors,
  mapAggregateToValidationInput,
} from '../../utils/monthlyPrime.utils';

export type MonthlyPrimeProps = {
  tamEmail?: string;
  referenceDate?: Date;
  preloadedMonthlyTamPrime?: MonthlyTamPrimeQueryResult;
};

const MonthlyPrime = ({
  tamEmail: tamEmailOverride,
  referenceDate: referenceDateOverride,
  preloadedMonthlyTamPrime,
}: MonthlyPrimeProps) => {
  const { t } = useTranslation(['primes', 'random']);
  const navigate = useNavigate();
  const auth = useAuthStore((state) => state.auth);
  const { getTamDisplayName: getActiveTamDisplayName } = useActiveTams();
  const authTamEmail = typeof auth?.user?.email === 'string' ? auth.user.email.trim() : '';
  const isSystem = auth?.user?.roles?.includes(Role.System) || false;
  const isTamPreview = Boolean(tamEmailOverride);
  const tamEmail = isTamPreview ? tamEmailOverride?.trim() : authTamEmail;
  const defaultReferenceDate = useMemo(() => getDefaultPrimeReferenceDate(), []);
  const referenceDate = referenceDateOverride ?? defaultReferenceDate;
  const monthLabel = formatMonthYearFr(referenceDate);
  const tamDisplayName = isTamPreview ? getActiveTamDisplayName(tamEmail ?? '') : getTamDisplayName(auth?.user);
  const [regularisationModalOpen, setRegularisationModalOpen] = useState(false);

  const { loading, error, data, refetch, networkStatus } = useQuery<GetMonthlyTamPrimeQueryData>(
    GET_MONTHLY_TAM_PRIME,
    {
      skip: !tamEmail,
      variables: {
        tamEmail,
        referenceDate,
      },
      context: { clientName: 'api.talents' },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );

  const freshMonthlyAggregate = data?.getMonthlyTamPrime;

  const monthlyAggregate = useMemo(
    () => freshMonthlyAggregate ?? preloadedMonthlyTamPrime,
    [freshMonthlyAggregate, preloadedMonthlyTamPrime],
  );

  const isMonthlyLoading = useMemo(
    () => loading && preloadedMonthlyTamPrime === undefined,
    [loading, preloadedMonthlyTamPrime],
  );

  const isRefetching = networkStatus === NetworkStatus.refetch;

  const isAwaitingFreshMonthlyPrime =
    preloadedMonthlyTamPrime !== undefined && freshMonthlyAggregate === undefined && loading;

  const isHeaderLoading = isMonthlyLoading || isRefetching || isAwaitingFreshMonthlyPrime;

  const headerAmountsSource = isHeaderLoading ? undefined : freshMonthlyAggregate ?? preloadedMonthlyTamPrime;

  const isValidatedMonthly = useMemo(
    () => monthlyAggregate !== undefined && isValidatedMonthlyTamPrime(monthlyAggregate),
    [monthlyAggregate],
  );

  const canManageRegularisation = isSystem && monthlyAggregate !== undefined && !isHeaderLoading && !isValidatedMonthly;

  const hasExistingRegularisation = useMemo(
    () => (headerAmountsSource?.regularisation ?? 0) !== 0,
    [headerAmountsSource],
  );

  const [validateMonthlyPrime, { loading: isValidating }] = useMutation(VALIDATE_MONTHLY_PRIME, {
    context: { clientName: 'api.talents' },
    onCompleted: () => {
      notify('success', t('validateSuccess'));
      navigate('/primes/annuel');
    },
    onError: (mutationError) => {
      errorLogger(mutationError, { extraInfos: 'validateMonthlyPrime failed on /primes/mois' });
      notify('error', t('validateError'), mutationError);
    },
  });

  const handleRegularisationSaved = useCallback(() => {
    void refetch().catch((refreshError) => {
      errorLogger(refreshError, {
        extraInfos: 'getMonthlyTamPrime refresh failed after regularisation save',
      });
      notify('error', t('loadError'), refreshError);
    });
  }, [refetch, t]);

  const handleValidate = useCallback(() => {
    if (!monthlyAggregate || !canValidateMonthlyPrime(monthlyAggregate)) {
      return;
    }
    void validateMonthlyPrime({
      variables: {
        tamEmail,
        referenceDate,
        prime: mapAggregateToValidationInput(monthlyAggregate),
      },
    });
  }, [monthlyAggregate, referenceDate, tamEmail, validateMonthlyPrime]);

  if (!tamEmail) {
    return <ErrorMessage variant="body1">{t('missingTamEmail')}</ErrorMessage>;
  }

  if (error) {
    errorLogger(error, { extraInfos: 'getMonthlyTamPrime failed on /primes' });
    return <ErrorMessage variant="body1">{t('loadError')}</ErrorMessage>;
  }

  const primes: TamPrimeLine[] = isMonthlyLoading ? [] : monthlyAggregate?.primes ?? [];
  const amounts = headerAmountsSource ? deriveHeaderAmounts(headerAmountsSource) : undefined;
  const canValidate = !isMonthlyLoading && monthlyAggregate ? canValidateMonthlyPrime(monthlyAggregate) : false;
  const validationDate =
    isValidatedMonthly && monthlyAggregate !== undefined && isValidatedMonthlyTamPrime(monthlyAggregate)
      ? monthlyAggregate.validationDate
      : undefined;
  const validateDisabledTooltip =
    !isMonthlyLoading && !isValidating && monthlyAggregate !== undefined && !canValidate
      ? hasPrimeLineErrors(monthlyAggregate)
        ? t('validateDisabledErrorLineTooltip')
        : hasIneligibleMissionCras(monthlyAggregate)
        ? t('validateDisabledCraTooltip')
        : undefined
      : undefined;

  return (
    <Container>
      <MonthlyPrimeHeader
        tamDisplayName={tamDisplayName}
        monthLabel={monthLabel}
        amounts={amounts}
        loading={isHeaderLoading}
        isValidated={isValidatedMonthly}
        validationDate={validationDate}
        canValidate={canValidate}
        isValidating={isValidating}
        validateDisabledTooltip={validateDisabledTooltip}
        onValidate={handleValidate}
        canAddRegularisation={canManageRegularisation}
        hasExistingRegularisation={hasExistingRegularisation}
        onAddRegularisation={() => setRegularisationModalOpen(true)}
        showValidateButton={!isSystem}
      />
      <TamRegularisationModal
        open={regularisationModalOpen}
        onClose={() => setRegularisationModalOpen(false)}
        onSaved={handleRegularisationSaved}
        tamEmail={tamEmail}
        tamDisplayName={tamDisplayName}
        referenceDate={referenceDate}
      />
      <MonthlyPrimeTable rows={primes} loading={isMonthlyLoading} />
    </Container>
  );
};

export default MonthlyPrime;

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  color: theme.palette.error.main,
}));
